import classNames from "classnames";
import { Tabs } from "../../shared";
import { useTabPanelsOverflow } from "../../hooks/useTabPanelsOverflow";

const LAYOUT_PRESETS = {
  /**
   * Full-page split layout (e.g. Job Show inside DashboardLayout).
   * Scroll lives on the tab panel container; padding on panel items.
   */
  page: {
    className: "w-full h-full flex flex-col",
    panelClasses: "border-t",
    panelItemClasses: "flex-1 min-h-0 p-2 pt-0",
    tabListClasses: "bg-white px-2 h-[38px] flex-shrink-0",
    tabclassName: "mx-1",
    fullHeight: true,
  },
  /**
   * Fixed-width drawer / slide-over (e.g. Dispatch event panel).
   */
  panel: {
    className: "h-full min-h-0 flex flex-col",
    panelClasses: "mt-4 min-h-0",
    panelItemClasses: "flex-1 min-h-0",
    tabListClasses: "bg-white h-[38px] flex-shrink-0",
    tabclassName: "",
    tabClass:
      "outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
    fullHeight: true,
    panelStyle: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overscrollBehavior: "contain",
    },
  },
};

/**
 * Tabs configured for constrained flex parents with optional overflow padding.
 *
 * @param {Object} props
 * @param {'page'|'panel'} [props.layout='page']
 * @param {Object} [props.wrapperStyle]
 * @param {Object} [props.panelStyle]
 */
export const ConstrainedTabs = ({
  layout = "page",
  wrapperStyle,
  panelStyle,
  className,
  panelClasses,
  panelItemClasses,
  tabListClasses,
  tabclassName,
  tabClass,
  fullHeight,
  ...tabsProps
}) => {
  const preset = LAYOUT_PRESETS[layout] || LAYOUT_PRESETS.page;
  const { panelsRef, handlePanelsLayout, panelStyleOverrides } =
    useTabPanelsOverflow();

  return (
    <Tabs
      {...preset}
      {...tabsProps}
      className={classNames(preset.className, className)}
      panelClasses={classNames(preset.panelClasses, panelClasses)}
      panelItemClasses={classNames(preset.panelItemClasses, panelItemClasses)}
      tabListClasses={classNames(preset.tabListClasses, tabListClasses)}
      tabclassName={tabclassName ?? preset.tabclassName}
      tabClass={tabClass ?? preset.tabClass}
      fullHeight={fullHeight ?? preset.fullHeight}
      style={wrapperStyle}
      panelsRef={panelsRef}
      onPanelsLayout={handlePanelsLayout}
      panelStyle={{
        ...preset.panelStyle,
        ...panelStyle,
        ...panelStyleOverrides,
      }}
    />
  );
};

export default ConstrainedTabs;
